import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const PageHero = ({ image, alt, overline, title, subtitle, position = "object-center", parallax = true, heightClass = "h-[58svh] min-h-[420px] md:h-[66svh]" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Gentle parallax only — no upscaling, so the photo stays sharp.
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  return (
    <section
      ref={ref}
      data-testid="page-hero"
      className={`relative ${heightClass} w-full overflow-hidden bg-ink`}
    >
      {parallax ? (
        <motion.div style={{ y }} className="absolute -inset-[6%] will-change-transform">
          <img src={image} alt={alt} className={`w-full h-full object-cover ${position}`} />
        </motion.div>
      ) : (
        <img src={image} alt={alt} className={`absolute inset-0 w-full h-full object-cover ${position}`} />
      )}

      <div className="absolute inset-0 hero-mask" />

      <div className="relative z-10 h-full w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-20">
        {overline && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="overline text-canvas/80 mb-5"
          >
            {overline}
          </motion.p>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="serif text-canvas font-light tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.0]"
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="mt-6 max-w-xl text-canvas/85 text-base md:text-lg leading-relaxed font-light"
          >
            {subtitle}
          </motion.p>
        )}
      </div>
    </section>
  );
};
